/**
 * Sticky Note Editing Plugin
 *
 * Wraps the content-editable plugin for text editing and adds a color toolbar
 * with pastel color swatches. Selecting a swatch instantly changes the sticky
 * note background and notifies the app via onColorChange callback.
 */

import type {
  EditingPlugin,
  EditingPluginContext,
  EditingSession,
} from "../editing";
import type { ToolbarEntry } from "../toolbar";
import { createContentEditablePlugin, type ContentEditablePluginOptions } from "./content-editable";

// =============================================================================
// Color palette
// =============================================================================

export const STICKY_NOTE_COLORS: { id: string; label: string; color: string }[] = [
  { id: 'yellow', label: 'Yellow', color: '#fff9b1' },
  { id: 'blue',   label: 'Blue',   color: '#b3e5fc' },
  { id: 'green',  label: 'Green',  color: '#c8e6c9' },
  { id: 'pink',   label: 'Pink',   color: '#f8bbd0' },
  { id: 'orange', label: 'Orange', color: '#ffe0b2' },
  { id: 'purple', label: 'Purple', color: '#e1bee7' },
  { id: 'gray',   label: 'Gray',   color: '#e0e0e0' },
];

export const DEFAULT_STICKY_COLOR = '#fff9b1';

// =============================================================================
// Plugin options
// =============================================================================

export interface StickyNoteEditingPluginOptions {
  /** Passed through to the content-editable plugin (e.g., font-size fitting) */
  onInput?: ContentEditablePluginOptions['onInput'];
  /** Called when the user selects a color swatch */
  onColorChange?: (nodeId: string, color: string) => void;
}

// =============================================================================
// Plugin factory
// =============================================================================

/**
 * Create a sticky-note editing plugin with color toolbar.
 */
export function createStickyNoteEditingPlugin(
  options?: StickyNoteEditingPluginOptions,
): EditingPlugin {
  // Delegate text editing to the content-editable plugin
  const textPlugin = createContentEditablePlugin(
    ['sticky-note'],
    '.sticky-note-text',
    { onInput: options?.onInput },
  );

  return {
    nodeTypes: ['sticky-note'],
    editableSelector: '.sticky-note-text',

    startEditing(ctx: EditingPluginContext): EditingSession {
      // Start the text editing session
      const textSession = textPlugin.startEditing(ctx);

      // Read current color from node data
      const nodeState = ctx.getNodeState();
      const obj = (nodeState?.data as Record<string, unknown>)?.obj as Record<string, unknown> | undefined;
      let activeColor = (obj?.color as string) ?? DEFAULT_STICKY_COLOR;

      // Toolbar change subscribers
      let toolbarCallback: (() => void) | null = null;

      function buildToolbarEntries(): ToolbarEntry[] {
        return STICKY_NOTE_COLORS.map((c) => ({
          type: 'button' as const,
          id: `color-${c.id}`,
          label: c.label,
          title: c.label,
          color: c.color,
          isActive: activeColor === c.color,
          onActivate: () => {
            activeColor = c.color;

            // Instant visual feedback: set background on the .canvas-node element
            ctx.nodeElement.style.backgroundColor = c.color;

            // Write color into the live node data so the renderer sees it on re-render
            const state = ctx.getNodeState();
            if (state) {
              const dataObj = state.data as Record<string, unknown>;
              const innerObj = dataObj?.obj as Record<string, unknown> | undefined;
              if (innerObj) {
                innerObj.color = c.color;
              } else if (dataObj) {
                dataObj.color = c.color;
              }
            }

            // Notify the app (for SDK persistence, etc.)
            options?.onColorChange?.(ctx.nodeId, c.color);

            // Update toolbar active state
            toolbarCallback?.();
          },
        }));
      }

      return {
        getContent: () => textSession.getContent(),

        stop: (save: boolean) => textSession.stop(save),

        onCameraChange: textSession.onCameraChange,

        getToolbar: () => buildToolbarEntries(),

        onToolbarChange: (callback: () => void) => {
          toolbarCallback = callback;
          return () => { toolbarCallback = null; };
        },
      };
    },
  };
}
